// AI Signals API Service Layer with Real Data Integration
import { supabase } from "@/integrations/supabase/client";
import { aiBackendClient } from "./ai-backend-client";
import { realMarketDataService } from "./real-market-data";
import { realAISignalsEngine } from "./real-ai-signals-engine";
import { realRiskAnalytics } from "./real-risk-analytics";
import { signalHistoryTracker } from "./signal-history-tracker";
import { buildBondingCurveTradeTransaction } from "@/lib/solana-transactions";
import { mockDataService } from "./mock-data-service";
import { Connection, PublicKey } from "@solana/web3.js";
import { Wallet } from "@solana/wallet-adapter-react";

export interface AISignal {
  id: string;
  symbol: string;
  signal_type: 'BUY' | 'SELL' | 'HOLD';
  confidence_score: number;
  confidence_level: 'HIGH' | 'MEDIUM' | 'LOW';
  price_target?: number;
  current_price: number;
  entry_price?: number;
  stop_loss?: number;
  take_profit?: number;
  risk_score: number;
  time_horizon: 'IMMEDIATE' | 'SHORT_TERM' | 'MEDIUM_TERM' | 'LONG_TERM';
  model_name: string;
  model_version: string;
  reasoning: string;
  technical_indicators: {
    rsi?: number;
    macd?: number;
    bollinger_position?: number;
    volume_analysis?: string;
    support_resistance?: {
      support: number;
      resistance: number;
    };
  };
  market_sentiment?: {
    overall: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
    fear_greed_index?: number;
    social_sentiment?: number;
  };
  portfolio_impact?: {
    recommended_allocation: number;
    risk_adjustment: number;
    diversification_score: number;
  };
  status: 'ACTIVE' | 'EXECUTED' | 'EXPIRED' | 'CANCELLED';
  created_at: string;
  updated_at: string;
  expires_at?: string;
  executed_at?: string;
  performance_metrics?: {
    profit_loss?: number;
    accuracy?: number;
    max_drawdown?: number;
    sharpe_ratio?: number;
  };
}

export interface SignalHistory {
  signal_id: string;
  date: string;
  price: number;
  confidence: number;
  accuracy?: number;
  actual_return?: number;
}

export interface ModelPerformance {
  model_name: string;
  model_version: string;
  total_signals: number;
  accuracy_rate: number;
  avg_confidence: number;
  profitable_signals: number;
  total_return: number;
  max_drawdown: number;
  sharpe_ratio: number;
  win_rate: number;
  avg_holding_period: number;
  last_updated: string;
}

export interface RiskMetrics {
  overall_portfolio_risk: number;
  signal_concentration_risk: number;
  market_correlation_risk: number;
  volatility_risk: number;
  liquidity_risk: number;
  risk_score_breakdown: {
    factor: string;
    score: number;
    impact: 'LOW' | 'MEDIUM' | 'HIGH';
  }[];
  recommended_adjustments: {
    type: 'REDUCE' | 'INCREASE' | 'HOLD';
    asset: string;
    current_allocation: number;
    recommended_allocation: number;
    reason: string;
  }[];
}

export interface PortfolioOptimization {
  current_allocation: Record<string, number>;
  recommended_allocation: Record<string, number>;
  risk_reduction: number;
  expected_improvement: number;
  rebalancing_actions: {
    action: 'BUY' | 'SELL' | 'HOLD';
    asset: string;
    amount: number;
    reason: string;
  }[];
  diversification_score: {
    before: number;
    after: number;
    improvement: number;
  };
}

export interface SignalFilters {
  symbol?: string;
  signal_type?: 'BUY' | 'SELL' | 'HOLD';
  confidence_level?: 'HIGH' | 'MEDIUM' | 'LOW';
  risk_score_min?: number;
  risk_score_max?: number;
  time_horizon?: 'IMMEDIATE' | 'SHORT_TERM' | 'MEDIUM_TERM' | 'LONG_TERM';
  model_name?: string;
  status?: 'ACTIVE' | 'EXECUTED' | 'EXPIRED' | 'CANCELLED';
  date_from?: string;
  date_to?: string;
}

export interface SignalsResponse {
  signals: AISignal[];
  total_count: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

class AISignalsAPI {
  private baseUrl = '/api/ai-signals';

  // Fetch signals with filtering and pagination (using Supabase Edge Functions)
  async getSignals(
    filters: SignalFilters = {},
    page = 1,
    perPage = 20,
    sortBy = 'created_at',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<SignalsResponse> {
    try {
      // Call Supabase Edge Function for signals
      const { data, error } = await supabase.functions.invoke('signals', {
        body: {
          action: 'get_signals',
          filters,
          page,
          per_page: perPage,
          sort_by: sortBy,
          sort_order: sortOrder
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.warn('Supabase Edge Function failed, trying AI backend:', error);
      
      try {
        // Try AI backend as fallback
        const signals = await aiBackendClient.getLatestSignals(perPage);
        return {
          signals,
          total_count: signals.length,
          page,
          per_page: perPage,
          has_next: false,
          has_prev: page > 1
        };
      } catch (backendError) {
        console.warn('AI backend failed, using mock data:', backendError);
        
        // Use mock data as final fallback
        if (mockDataService.shouldUseMockData()) {
          await mockDataService.simulateDelay();
          const mockSignals = mockDataService.getMockSignals(perPage);
          return {
            signals: mockSignals,
            total_count: mockSignals.length,
            page,
            per_page: perPage,
            has_next: false,
            has_prev: page > 1
          };
        }
        
        throw new Error('All data sources failed');
      }
    }
  }

  // Get a specific signal by ID
  async getSignal(signalId: string): Promise<AISignal> {
    try {
      // Call Supabase Edge Function for specific signal
      const { data, error } = await supabase.functions.invoke('signals', {
        body: {
          action: 'get_signal',
          signal_id: signalId
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching signal:', error);
      throw new Error('Failed to fetch signal');
    }
  }

  // Get signal history for performance analysis
  async getSignalHistory(signalId: string): Promise<SignalHistory[]> {
    try {
      const { data, error } = await supabase
        .from('signal_history')
        .select('*')
        .eq('signal_id', signalId)
        .order('date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching signal history:', error);
      throw new Error('Failed to fetch signal history');
    }
  }

  // Get AI model performance metrics
  async getModelPerformance(): Promise<ModelPerformance[]> {
    try {
      // Call Supabase Edge Function for model performance
      const { data, error } = await supabase.functions.invoke('models-performance', {
        body: {
          action: 'get_performance'
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching model performance:', error);
      throw new Error('Failed to fetch model performance');
    }
  }

  // Get risk metrics for current portfolio
  async getRiskMetrics(holdings?: Record<string, number>): Promise<RiskMetrics> {
    try {
      // Call Supabase Edge Function for risk metrics
      const { data, error } = await supabase.functions.invoke('risk-metrics', {
        body: {
          action: 'get_metrics',
          holdings
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching risk metrics:', error);
      throw new Error('Failed to fetch risk metrics');
    }
  }

  // Get portfolio optimization recommendations (using real optimization)
  async getPortfolioOptimization(holdings?: Record<string, number>): Promise<PortfolioOptimization> {
    try {
      // If no holdings provided, get demo portfolio
      const portfolio = holdings || {
        'BTC': 25000,
        'ETH': 15000,
        'SOL': 5000,
        'ADA': 2000,
        'DOT': 1500
      };

      // Calculate real portfolio optimization
      const optimization = await realRiskAnalytics.optimizePortfolio(portfolio);
      
      // Store in database for history
      await realRiskAnalytics.storePortfolioOptimization(optimization);
      
      return optimization;
    } catch (error) {
      console.error('Error calculating portfolio optimization:', error);
      
      // Fallback to database data
      try {
        const { data, error: dbError } = await supabase
          .from('portfolio_optimization')
          .select('*')
          .order('calculated_at', { ascending: false })
          .limit(1)
          .single();

        if (dbError) throw dbError;
        return data;
      } catch (fallbackError) {
        console.error('Error fetching portfolio optimization from database:', fallbackError);
        throw new Error('Failed to fetch portfolio optimization');
      }
    }
  }

  // Calculate risk-adjusted position sizing based on signal recommendations
  private calculateRiskAdjustedPosition(signal: AISignal): { amount: number; riskLevel: string; reasoning: string } {
    // Base position size on portfolio impact recommendations
    const baseAllocation = signal.portfolio_impact?.recommended_allocation || 0.05; // 5% default
    const riskAdjustment = signal.portfolio_impact?.risk_adjustment || 1.0;

    // Adjust based on confidence and risk score
    const confidenceMultiplier = signal.confidence_score;
    const riskMultiplier = Math.max(0.1, 1 - (signal.risk_score / 100)); // Lower risk score = higher multiplier

    // Calculate final position size (in SOL for buys, token amount for sells)
    const positionSize = baseAllocation * riskAdjustment * confidenceMultiplier * riskMultiplier;

    // Determine risk level
    let riskLevel = 'MEDIUM';
    if (positionSize < 0.01) riskLevel = 'LOW';
    else if (positionSize > 0.1) riskLevel = 'HIGH';

    return {
      amount: Math.max(0.001, positionSize), // Minimum 0.001 SOL
      riskLevel,
      reasoning: `Base allocation: ${(baseAllocation * 100).toFixed(1)}%, Risk adjustment: ${(riskAdjustment * 100).toFixed(1)}%, Confidence: ${(confidenceMultiplier * 100).toFixed(1)}%, Risk multiplier: ${(riskMultiplier * 100).toFixed(1)}%`
    };
  }

  // Log signal execution for tracking and analytics
  private async logSignalExecution(signalId: string, tradeResult: any, positionSize: any): Promise<void> {
    try {
      await supabase
        .from('signal_execution_log')
        .insert({
          signal_id: signalId,
          trade_id: tradeResult.tradeId,
          transaction_hash: tradeResult.txSignature,
          position_size: positionSize.amount,
          risk_level: positionSize.riskLevel,
          execution_reasoning: positionSize.reasoning,
          trade_result: tradeResult,
          executed_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Error logging signal execution:', error);
      // Don't throw - logging failure shouldn't break execution
    }
  }

  // Execute a signal using Supabase Edge Function
  async executeSignal(
    signalId: string,
    userWallet?: string
  ): Promise<{ success: boolean; transaction_id?: string; trade_result?: any; error?: string }> {
    try {
      // Call Supabase Edge Function for signal execution
      const { data, error } = await supabase.functions.invoke('signals', {
        body: {
          action: 'execute_signal',
          signal_id: signalId,
          user_wallet: userWallet
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error executing signal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to execute signal'
      };
    }
  }

  // Cancel a signal
  async cancelSignal(signalId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('ai_signals')
        .update({ 
          status: 'CANCELLED',
          updated_at: new Date().toISOString()
        })
        .eq('id', signalId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error cancelling signal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to cancel signal'
      };
    }
  }

  // Get available models
  async getAvailableModels(): Promise<{ name: string; version: string; description: string; accuracy: number }[]> {
    try {
      const { data, error } = await supabase
        .from('ai_models')
        .select('*')
        .eq('status', 'ACTIVE');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching available models:', error);
      throw new Error('Failed to fetch available models');
    }
  }

  // Get real-time signal statistics
  async getSignalStats(): Promise<{
    total_signals: number;
    active_signals: number;
    executed_today: number;
    success_rate: number;
    avg_confidence: number;
    top_performing_model: string;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const [signalsResult, activeResult, executedResult, performanceResult] = await Promise.all([
        supabase.from('ai_signals').select('id', { count: 'exact' }),
        supabase.from('ai_signals').select('id', { count: 'exact' }).eq('status', 'ACTIVE'),
        supabase.from('ai_signals').select('id', { count: 'exact' }).gte('created_at', today),
        supabase.from('signal_performance').select('*').order('success_rate', { ascending: false }).limit(1)
      ]);

      const totalSignals = signalsResult.count || 0;
      const activeSignals = activeResult.count || 0;
      const executedToday = executedResult.count || 0;
      const topModel = performanceResult.data?.[0]?.model_name || 'N/A';

      // Calculate additional metrics
      const { data: allSignals } = await supabase
        .from('ai_signals')
        .select('confidence_score, status')
        .limit(totalSignals);

      const avgConfidence = allSignals?.reduce((sum, s) => sum + s.confidence_score, 0) / (allSignals?.length || 1) || 0;
      
      const executedSignals = allSignals?.filter(s => s.status === 'EXECUTED').length || 0;
      const successRate = executedSignals > 0 ? (executedSignals / (allSignals?.filter(s => ['EXECUTED', 'EXPIRED'].includes(s.status)).length || 1)) * 100 : 0;

      return {
        total_signals: totalSignals,
        active_signals: activeSignals,
        executed_today: executedToday,
        success_rate: Math.round(successRate * 100) / 100,
        avg_confidence: Math.round(avgConfidence * 100) / 100,
        top_performing_model: topModel,
      };
    } catch (error) {
      console.warn('Database query failed, using mock data for stats:', error);
      
      // Use mock data as fallback
      if (mockDataService.shouldUseMockData()) {
        await mockDataService.simulateDelay(200);
        return mockDataService.getMockSignalStats();
      }
      
      throw new Error('Failed to fetch signal statistics');
    }
  }
}

export const aiSignalsAPI = new AISignalsAPI();